/**
 * Closes the browser-history gap around sign-in/sign-out: if user A signs
 * out and user B signs in on the same tab, B can press Back and land on a
 * URL A visited while authenticated. AuthGuard's own `if (!user)` check
 * doesn't catch this - it only asks "is *someone* signed in," which is
 * true for both "B navigated here" and "the browser popped back into one
 * of A's old entries," since both simply have a truthy user.
 *
 * Tracks, per tab (sessionStorage - history is itself per-tab, so this
 * deliberately isn't shared across tabs), which `location.key`s were
 * reached by the *currently* signed-in user going forward (PUSH/REPLACE).
 * A POP (Back/Forward) into a key that was never recorded that way
 * predates this user's session and gets sealed off. Switching user ids
 * resets the known-key set, since none of the previous user's keys belong
 * to the new one; authStore.logout() also resets it directly so the same
 * user signing back out and in later starts from a clean boundary too.
 */

const USER_KEY = "il.authSession.userId";
const KEYS_KEY = "il.authSession.keys";

function readKeys(): string[] {
  try {
    const raw = sessionStorage.getItem(KEYS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeKeys(keys: string[]): void {
  try {
    sessionStorage.setItem(KEYS_KEY, JSON.stringify(keys));
  } catch {
    /* fails open - see shouldSealHistoryEntry */
  }
}

/**
 * Call once per render of a protected route. Returns true when the
 * current entry should be sealed off (redirected to /login) because it's
 * a Back/Forward landing on a URL from a previous user's session.
 *
 * Fails open (never seals) if sessionStorage is unavailable (e.g. Safari
 * private browsing) - this closes one specific leak, it shouldn't risk
 * bouncing every navigation to /login for everyone if storage throws.
 */
export function shouldSealHistoryEntry(params: {
  userId: string;
  locationKey: string;
  isPop: boolean;
}): boolean {
  const { userId, locationKey, isPop } = params;

  let storedUserId: string | null;
  try {
    storedUserId = sessionStorage.getItem(USER_KEY);
  } catch {
    return false;
  }

  if (storedUserId !== userId) {
    // First entry we've seen for this user id this tab, or a different
    // user just signed in - none of the previously-recorded keys are
    // this user's, so anchor a fresh set on the entry they're on now.
    try {
      sessionStorage.setItem(USER_KEY, userId);
    } catch {
      return false;
    }
    writeKeys([locationKey]);
    return false;
  }

  const keys = readKeys();
  if (isPop) return !keys.includes(locationKey);
  if (!keys.includes(locationKey)) writeKeys([...keys, locationKey]);
  return false;
}

/** Called from authStore.logout() so the next sign-in - same user or not - starts a clean boundary. */
export function clearAuthSessionHistory(): void {
  try {
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(KEYS_KEY);
  } catch {
    /* non-fatal */
  }
}
